import React, { useState } from 'react';
import { Calendar, Users, MapPin, Mic, CheckCircle, Star, XCircle } from 'lucide-react';
import PageLayout, { PageHero, PageSection, PageCTA } from '../components/common/PageLayout';
import SEOHead from '../components/common/SEOHead';
import { useLanguage } from '../i18n/LanguageContext';

const EVENT_GALLERY = [
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/6lghk188_6D5BE1DD-A003-4661-8A21-A95E8DAF5F00.png', title: 'Evento corporativo' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/d3034cg8_6A963654-19C7-4164-B47B-66702E2CBB17.png', title: 'Evento masivo' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/1t6l7xrw_784188E3-94EC-4024-AA54-F027CA0D9874.png', title: 'Boda premium' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/kvyua9ch_F3E666D8-2848-43FE-960B-B28ACE0CE2B7.png', title: 'Festival' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/rvc6k1k9_D9E80893-54A2-4E3E-8B6C-CF39D72069E1.png', title: 'Lanzamiento' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/zio3060d_9FD265EA-9199-4903-B7B9-E7CA7AB0CF82.png', title: 'Inauguración' },
];

const eventTypeIcons = [Calendar, Users, Mic, MapPin];
const eventTypeKeys = ['corporate', 'weddings', 'concerts', 'public'];

const DronesParaEventosPage = () => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const { t, language } = useLanguage();
  const page = t('seoPages.dronesForEvents');

  return (
    <PageLayout>
      <SEOHead 
        title={language === 'es' ? "Drones para Eventos en México | Shows Aéreos Profesionales | NexAir" : "Drones for Events in Mexico | Professional Aerial Shows | NexAir"}
        description={language === 'es' ? "Drones para todo tipo de eventos en México: bodas, corporativos, conciertos, festivales y celebraciones. Shows personalizados con cientos de drones sincronizados." : "Drones for all types of events in Mexico: weddings, corporate, concerts, festivals and celebrations. Customized shows with hundreds of synchronized drones."}
        keywords={language === 'es' ? "drones para eventos, show de drones boda, drones corporativo, drones festival, drones concierto, evento con drones mexico" : "drones for events, drone show wedding, corporate drones, festival drones, concert drones, event with drones mexico"}
        canonicalUrl="https://nexair.com.mx/drones-para-eventos"
      />
      
      <PageHero 
        image="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/d3034cg8_6A963654-19C7-4164-B47B-66702E2CBB17.png"
        title={page.title}
        subtitle={page.subtitle}
        description={page.description}
      />
      
      {/* Event Types */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
          {page.eventTypesTitle} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{page.eventTypesHighlight}</span> {page.eventTypesSuffix}
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          {eventTypeKeys.map((key, index) => {
            const Icon = eventTypeIcons[index];
            const eventType = page.eventTypes[key];
            return (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-cyan-500/50 transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-cyan-500/20 to-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-7 h-7 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{eventType.title}</h3>
                    <p className="text-gray-400">{eventType.desc}</p>
                  </div>
                </div>
                <div className="space-y-2 mt-4 pl-4 border-l-2 border-cyan-500/30">
                  {eventType.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-300 text-sm">
                      <Star className="w-4 h-4 text-cyan-400" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </PageSection>
      
      {/* Why Drones */}
      <PageSection className="bg-[#050a15]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
          {page.whyDronesTitle}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {page.whyDrones.map((item, index) => (
            <div key={index} className="flex items-start gap-4">
              <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </PageSection>
      
      {/* Gallery */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
          {page.galleryTitle}
        </h2>
        <p className="text-gray-400 text-center mb-8 max-w-2xl mx-auto">
          {page.galleryDesc}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {EVENT_GALLERY.map((image, index) => (
            <div 
              key={index}
              className="aspect-video rounded-xl overflow-hidden border border-white/10 hover:border-cyan-500/50 transition-all cursor-pointer"
              onClick={() => setLightboxImage(image)}
            >
              <img 
                src={image.url} 
                alt={image.title}
                loading="lazy"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          ))}
        </div>
      </PageSection>
      
      {/* Pricing Tiers */}
      <PageSection className="bg-[#050a15]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
          {page.pricingTitle}
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { key: 'essential', drones: '100-200', duration: '8-12 min', highlight: false },
            { key: 'premium', drones: '200-500', duration: '8-12 min', highlight: true },
            { key: 'spectacular', drones: '500+', duration: '8-12 min', highlight: false },
          ].map((tier, index) => {
            const tierData = page.pricingTiers[tier.key];
            return (
              <div key={index} className={`bg-white/5 border rounded-2xl p-8 ${tier.highlight ? 'border-cyan-500 ring-2 ring-cyan-500/20' : 'border-white/10'}`}>
                {tier.highlight && (
                  <div className="text-cyan-400 text-sm font-semibold mb-4">{page.mostPopular}</div>
                )}
                <h3 className="text-2xl font-bold text-white mb-2">{tierData.name}</h3>
                <div className="text-cyan-400 text-lg mb-2">{tier.drones} {page.drones}</div>
                <div className="text-gray-400 mb-4">{tier.duration} {page.showDuration}</div>
                <div className="text-gray-300 text-sm mb-6">{page.idealFor} {tierData.ideal}</div>
                <ul className="space-y-2">
                  {tierData.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-cyan-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <p className="text-center text-gray-400 mt-8">
          {page.pricingNote}
        </p>
      </PageSection>
      
      <PageCTA 
        title={page.ctaTitle}
        subtitle={page.ctaSubtitle}
        whatsappMessage={page.ctaWhatsapp}
      />

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <XCircle size={40} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img 
              src={lightboxImage.url} 
              alt={lightboxImage.title}
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-center">
              <h3 className="text-white text-xl font-bold">{lightboxImage.title}</h3>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default DronesParaEventosPage;
